<template>
  <div class="import-export-group">
    <el-button
      @click="tab = 'import'"
      :disabled="disabled">Import</el-button>
    <el-button
      @click="tab = 'export'"
      :disabled="disabled || unmutable">Export</el-button>

    <el-dialog class="import-export-dialog"
      title="Import / Export"
      :visible="!!tab"
      @update:visible="val => { if(!val) tab = null }"
      v-bind="$attrs">
      <el-tabs v-model="tab" type="border-card">
        <el-tab-pane label="Export" name="export">
          <el-input ref="exportValue"
            type="textarea"
            :value="exportValue"
            @click.native="selectExportValue"
            :rows="10"
          ></el-input>
          <el-button-group style="margin-top: 10px; justify-content: center;">
            <el-button
              type="primary"
              @click="copyExportValue"
            >Copy to clipboard</el-button>
            <el-button
              type="danger"
              @click="tab = null">
              Close export
            </el-button>
          </el-button-group>
        </el-tab-pane>
        <el-tab-pane label="Import" name="import" :disabled="unmutable">
          <el-form label-width="150px">
            <el-input
              type="textarea"
              v-model="importValue"
              :rows="10"
            ></el-input>
            <el-collapse-transition>
              <el-alert v-if="importError"
                type="error"
                :closable="false">
                <template slot="title">Cannot parse the import: {{ importError }}</template>
              </el-alert>
            </el-collapse-transition>
            <template v-if="importObject">
              <el-collapse-transition>
                <el-alert v-show="importSkill !== objectSkill"
                  type="warning"
                  :closable="false">
                  <template slot="title">This export comes from another <em>skill</em></template>
                </el-alert>
              </el-collapse-transition>
              <el-collapse-transition>
                <el-alert v-show="importSkill === objectSkill && existingIds[importId]"
                  type="warning"
                  :closable="false">
                  <template slot="title">This <em>{{ idField }}</em>{{' '}}<router-link target= "_blank"
                    :to="{
                      name: routeName || $route.name, query: $route.query,
                      params: { ...$route.params, [idField]: importId },
                    }"
                    >already exists</router-link></template>
                </el-alert>
              </el-collapse-transition>
              <template v-if="objectNew">
                <el-collapse-transition>
                  <el-alert v-show="importSpecial"
                    type="warning"
                    :closable="false">
                    <template slot="title">This export comes from a <em>special {{ idField }}</em></template>
                  </el-alert>
                </el-collapse-transition>
                <el-form-item label="Create with same id">
                  <el-checkbox
                    :value="!impossibleSameid && importSameid"
                    :disabled="impossibleSameid"
                    @change="val => importSameid = val"
                  >Same id</el-checkbox>
                </el-form-item>
              </template>
              <template v-else>
                <el-collapse-transition>
                  <el-alert v-show="importNew"
                    type="warning"
                    :closable="false">
                    <template slot="title">This export comes from an unsaved <em>{{ idField }}</em></template>
                  </el-alert>
                </el-collapse-transition>
                <el-collapse-transition>
                  <el-alert v-show="importSpecial && importId !== objectId"
                    type="warning"
                    :closable="false">
                    <template slot="title">This export comes from {{objectSpecial ? 'another' : 'a'}} <em>special {{ idField }}</em></template>
                  </el-alert>
                </el-collapse-transition>
                <el-collapse-transition>
                  <el-alert v-show="!importSpecial && objectSpecial"
                    type="warning"
                    :closable="false">
                    <template slot="title">This export comes from a <em>non-special {{ idField }}</em></template>
                  </el-alert>
                </el-collapse-transition>
                <el-collapse-transition>
                  <el-alert v-show="importSkill === objectSkill && !objectSpecial && !importNew && !importSpecial && importId !== objectId"
                    type="warning"
                    :closable="false">
                    <template slot="title">This export comes from another <em>{{ idField }}</em></template>
                  </el-alert>
                </el-collapse-transition>
              </template>
            </template>
            <el-button-group style="justify-content: center;">
              <el-button
                :disabled="!importObject"
                type="primary"
                @click="emitImport">
                Import the {{ idField }}
              </el-button>
              <el-button
                type="danger"
                @click="tab = null">
                Cancel import
              </el-button>
            </el-button-group>
          </el-form>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>
  </div>
</template>

<script>
const COPY_FIELDS = ['status', 'timestamp', 'published', 'approved']

export default {
  name: 'ImportExport',
  props: {
    skill: String,
    idField: String,
    objectId: String,
    value: Object,
    disabled: Boolean,
    unmutable: Boolean,
    existing: Array,
    routeName: String,
  },

  data () {
    return {
      tab: null,
      importValue: '',
      importSameid: true,
      importObject: null,
      importError: '',
      importSkill: null,
      importId: '',
      importNew: false,
      importSpecial: false,
    }
  },

  computed: {
    exportValue () {
      return JSON.stringify(this.value)
    },
    objectNew () {
      return !this.objectId || this.objectId === 'new'
    },
    objectSpecial () {
      return !this.objectNew && this.objectId.length !== 32
    },
    objectSkill () {
      return this.idField !== 'skill' ? this.skill || 'global' : null
    },
    existingIds () {
      let ids = {}
      let local = this.objectSkill !== 'global'
      for (let obj of this.existing || []) {
        if (local && obj.local === false) continue
        let objId = obj[this.idField]
        if (objId && objId !== this.objectId) {
          ids[objId] = true
        }
      }
      return ids
    },
    impossibleSameid () {
      return this.objectSkill && this.importSkill !== this.objectSkill ||
             this.importSpecial || this.importNew ||
             this.existingIds[this.importId]
    },
  },

  watch: {
    tab (val) {
      if (!val) {
        this.importValue = ''
        this.importSameid = true
      }
    },
    disabled (val) {
      if (val) this.tab = null
    },
    unmutable (val) {
      if (val && this.tab === 'import') this.tab = null
    },
    importValue (val) {
      this.importObject = null
      this.importError = ''
      if (!val) return
      try {
        val = JSON.parse(val)
      } catch (e) {
        this.importError = e.message
        return
      }
      if (typeof val !== 'object' || Array.isArray(val)) {
        this.importError = 'object expected'
        return
      }
      this.importObject = val
      this.importSkill = this.idField !== 'skill'
        ? val.skill || 'global' : null
      this.importId = val[this.idField] || 'new'
      this.importNew = !this.importId || this.importId === 'new'
      this.importSpecial = this.importId !== 'new' &&
                           this.importId.length !== 32
    },
  },

  methods: {
    selectExportValue () {
      const input = this.$refs.exportValue.$el.getElementsByTagName('textarea')[0]
      input.select()
      input.setSelectionRange(0, 999999999)
    },
    copyExportValue () {
      this.selectExportValue()
      document.execCommand('copy')
      this.$message('Intent export copied in clipboard.')
    },
    emitImport () {
      let data = { ...this.importObject }
      if (this.objectSkill) {
        data.skill = this.objectSkill
      }
      if (this.objectNew) {
        const sameid = !this.impossibleSameid && this.importSameid
        if (!sameid) delete data[this.idField]
        for (let field of COPY_FIELDS) {
          delete data[field]
        }
      } else {
        data[this.idField] = this.objectId
        for (let field of COPY_FIELDS) {
          data[field] = this.value[field]
        }
      }
      this.$emit('input', data)
      this.tab = null
    },
  },
}
</script>

<style lang="scss">
  @import "../element-variables.scss";

  .import-export-dialog .el-dialog {
    width: 90%;
    max-width: 700px;
    .el-alert, .el-button-group {
      margin-top: 10px
    }
  }
</style>
